/**
 * `viewerRole.ts` — "is this user CONFIRMED to be a non-author of the open
 * project?", in one place because two very different layers ask it.
 *
 * A VIEWER, for Campfire's purposes, is someone whose role on the open project
 * is neither owner nor editor. Two features turn on it, and both would be
 * actively harmful if they fired for an author:
 *
 *   - `WorkflowRunModel` starts the project's workflow for them by itself,
 *     whatever the author's autostart flags say, and returns them to the
 *     projects grid when it ends.
 *   - `useWorkflowPanelLayout` locks the run's floating panels in place, so the
 *     arrangement they see is the one the author stored. (Not the only reason
 *     that lock is set — a workflow declaring `lock_ui` locks them for the
 *     author too — but it is the only one this predicate answers for.)
 *
 * Two copies of a two-line predicate is exactly the drift this codebase keeps
 * warning about, hence this file.
 *
 * ⚠ IT READS `canBeAuthor()` — THE ROLE ALONE — AND NEVER `canAuthorProject()`.
 * That one ANDs in the PRESENTER term, which is transiently false for
 * everyone: a project's own owner is a non-presenter for the ~1 s until the
 * 1 Hz `ensurePresenter` self-elects them (the deliberate full-parity conjunct
 * `IPermissionsModel`'s header describes). So the presenter-flavoured predicate
 * reads an OWNER as a viewer on every project open. "Viewer" here is a fact
 * about the person's role on this project, not about who is driving right now.
 *
 * ⚠ AND IT REQUIRES `roleLoadState === "loaded"`. Every predicate on the
 * permissions model answers from the seeded LEAST privilege until the account
 * profile lands, and stays there when the fetch FAILS — which is
 * indistinguishable from a genuine Viewer. The model's own header states the
 * rule: a failure is not "Reader". Without this term a transient AppSync
 * hiccup would put an owner into guided playback and lock their panels.
 *
 * KNOWN residual window, recorded rather than papered over: `roleLoadState`
 * describes the ACCOUNT-PROFILE fetch, and the per-project role resolution
 * (`loadCurrentUserRoleAsync`) has no state flag of its own — so between the
 * profile landing and a shared-link project's role resolving, this reads the
 * user's natural account role. That is the same value branch (c) of
 * `resolveCurrentUserRoleAsync` produces, and a link role can only be clamped
 * DOWN by that licence, so the interim answer is never MORE permissive than the
 * final one. Give the permissions model a per-project resolution marker if that
 * ever stops being good enough.
 *
 * Note the two callers want opposite things from an UNKNOWN answer, and both
 * get them from `false`:
 *   - autostart reads false as "do not hijack this session", and
 *   - the panel lock reads false as "do not disable a control",
 * which is the safe direction in each case. Do not add a tri-state.
 */

import type { IPermissionsModel } from "./IPermissionsModel";

/**
 * True only when the role is RESOLVED and says this user cannot author the open
 * project. A null model (a headless run, or one registered without permissions)
 * is not a viewer.
 */
export function isConfirmedNonAuthor(permissions: IPermissionsModel | null): boolean {
  if (!permissions) return false;
  if (permissions.roleLoadState !== "loaded") return false;
  return !permissions.canBeAuthor();
}
